
import { Component, Watch } from "vue-property-decorator";
import RevisionsList from "@/components/Reusable/Revisions/RevisionsList.vue";
import Vue from "vue";
import {
  RevisionUiItem,
  SelectorsRevision,
  SelectorsRevisionsList
} from "@/types/revisions";
import { globalStore, uiStore } from "@/store/store-accessor";
import { getModuleNameFromMutation } from "@/store/util";
import defensiveCopy from "@/util/defensive-copy";
import { isEqual } from "lodash";
import { breakdanceUndo } from "@/lib/undo/undo";
import { fetchRemoteSelectorsRevisions } from "@/lib/selectors/revisions";

// TODO refactor code duplication w/GlobalSettingsPanelHistoryViewMode
@Component({
  components: { RevisionsList }
})
export default class SelectorsPanelHistoryViewMode extends Vue {
  isLoading = false;
  remoteRevisions: SelectorsRevisionsList | null = null;
  selectedIndex: number | null = null;
  unsubscribeFromStore = () => {};

  currentUnsavedRevision: SelectorsRevision | null = null;

  async beforeMount() {
    await this.loadRemoteRevisions();

    if (globalStore.selectorsHasUnsavedChangesPresent) {
      this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();

      /**
       * Without this condition, opening revisions list after Undoing/Redoing
       * revision selection will contain "Current" revision
       */
      if (this.selectedIndex === -1) {
        this.setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData();
      }
    }

    this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();

    this.unsubscribeFromStore = this.$store.subscribe(mutationPayload => {
      const moduleName = getModuleNameFromMutation(mutationPayload);

      if (
        moduleName === "global" &&
        mutationPayload.type !== "global/restoreSelectorsRevision" &&
        mutationPayload.type !== "global/setGlobalSettings" // should be ignored
      ) {
        // Undo/Redo
        if (mutationPayload.type === "global/setSelectors") {
          this.currentUnsavedRevision = null;
        } else {
          this.setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData();
        }

        this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();
      }
    });
  }

  beforeDestroy() {
    this.unsubscribeFromStore();
  }

  async loadRemoteRevisions() {
    this.isLoading = true;
    this.remoteRevisions = await fetchRemoteSelectorsRevisions();
    this.selectedIndex = null;
    this.isLoading = false;
  }

  recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch() {
    this.selectedIndex = this.findSelectedIndexBasedOnStoreDataMatch();
  }

  findSelectedIndexBasedOnStoreDataMatch(): number {
    const currentSelectors = defensiveCopy(globalStore.cssSelectors);

    return this.revisions.findIndex(revision => {
      return isEqual(revision.selectors, currentSelectors);
    });
  }

  setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData() {
    if (this.isCurrentStoreDataTheSameAsTheMostRecentRemoteRevisionData()) {
      this.currentUnsavedRevision = null;
    } else {
      this.setCurrentUnsavedRevisionFromCurrentStoreData();
    }
  }

  isCurrentStoreDataTheSameAsTheMostRecentRemoteRevisionData() {
    if (this.remoteRevisions === null) {
      return false;
    }

    const [mostRecentRemoteRevision] = this.remoteRevisions;
    const currentSelectors = defensiveCopy(globalStore.cssSelectors);

    return isEqual(mostRecentRemoteRevision.selectors, currentSelectors);
  }

  setCurrentUnsavedRevisionFromCurrentStoreData() {
    this.currentUnsavedRevision = {
      id: -1,
      author: false,
      date: "Current",
      selectors: defensiveCopy(globalStore.cssSelectors)
    };
  }

  get revisionUiItems(): RevisionUiItem<SelectorsRevision>[] {
    return this.revisions.map(
      (
        revision: SelectorsRevision,
        index: number
      ): RevisionUiItem<SelectorsRevision> => ({
        isSelected: index === this.selectedIndex,
        revisionData: revision
      })
    );
  }

  get revisions(): SelectorsRevision[] {
    const remoteRevisions = this.remoteRevisions ?? [];

    return this.currentUnsavedRevision
      ? [this.currentUnsavedRevision, ...remoteRevisions]
      : remoteRevisions;
  }

  onRevisionSelected(
    revisionUiItem: RevisionUiItem<SelectorsRevision>,
    index: number
  ) {
    this.selectedIndex = index;

    breakdanceUndo.transaction(
      () => globalStore.restoreSelectorsRevision(revisionUiItem.revisionData),
      "Restore Selectors From Revision"
    );
  }

  get unsavedChangesPresent() {
    return uiStore.unsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent")
  async updateRevisionsAfterSaving() {
    if (!uiStore.unsavedChangesPresent) {
      await this.loadRemoteRevisions();
      this.currentUnsavedRevision = null;
      this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();
    }
  }
}
